/**
 * Admin dashboard entry point.
 *
 * Bootstrap flow:
 * 1. Initialize admin API client with base URL
 * 2. Wrap in AdminAuthProvider + WorkspaceProvider
 * 3. Render admin App
 *
 * Separate from the companion app — admin has its own auth (JWT via OTP/Google),
 * its own API client (/api/admin/*), and its own layout.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { AdminAuthProvider } from './auth/AdminAuthProvider';
import { WorkspaceProvider } from './hooks/useWorkspace';
import { initAdminApiClient } from './api-client';
import { App } from './App';

// Import shared global styles (design tokens, tailwind, form-input, etc.)
import '@styles/globals.css';
import './admin-theme.css';

// ── Bootstrap ─────────────────────────────────────────────────────────

const container = document.getElementById('root');
if (!container) {
  throw new Error('Root element not found');
}

// Resolve admin API base URL:
// 1. Build-time env var override when explicitly configured
// 2. Same-origin /api/admin default for both prod and dev
//
// Front Door already routes admin.matcora.com/api/* to the Admin API
// Container App, so production does not need a separate build-time base URL.
const apiBaseUrl = import.meta.env.VITE_ADMIN_API_BASE_URL ?? '/api/admin';

initAdminApiClient(apiBaseUrl);
document.documentElement.setAttribute('data-admin-theme', 'matcora');

const root = createRoot(container);

root.render(
  <StrictMode>
    <BrowserRouter>
      <AdminAuthProvider>
        <WorkspaceProvider>
          <App />
        </WorkspaceProvider>
      </AdminAuthProvider>
    </BrowserRouter>
  </StrictMode>,
);
